/**
 * @engine/vfx - Spawn Context Nodes
 *
 * Nodes for controlling particle spawn rate and timing.
 */

import type { VFXNodeDefinition } from '../types';
import { dataPort, flowPort } from '../types';

/**
 * Spawn context node definitions.
 */
export const SPAWN_NODES: readonly VFXNodeDefinition[] = [
  // ---- Spawn Rate ----
  {
    id: 'vfx.spawn.rate',
    name: 'Spawn Rate',
    category: 'spawn',
    contexts: ['spawn'],
    description: 'Spawn particles at a constant rate (particles/second)',
    color: '#4CAF50',
    inputs: [
      dataPort('rate', 'Rate', 'float', { defaultValue: 10, range: { min: 0, max: 100_000_000 } }),
    ],
    outputs: [
      flowPort('spawn', 'Spawn'),
    ],
    modifiesParticle: false,
    // Spawn configuration is extracted by the system extractor (CPU-side +
    // spawn shader) — spawn nodes carry configuration, not shader code.
  },

  // ---- Variable Rate ----
  {
    id: 'vfx.spawn.variable_rate',
    name: 'Variable Rate Random',
    category: 'spawn',
    contexts: ['spawn'],
    description: 'Spawn particles with random rate between min and max each frame',
    color: '#4CAF50',
    inputs: [
      dataPort('rateMin', 'Rate Min', 'float', { defaultValue: 0, range: { min: 0, max: 100_000_000 } }),
      dataPort('rateMax', 'Rate Max', 'float', { defaultValue: 100, range: { min: 0, max: 100_000_000 } }),
    ],
    outputs: [
      flowPort('spawn', 'Spawn'),
    ],
    modifiesParticle: false,
  },

  // ---- Burst ----
  {
    id: 'vfx.spawn.burst',
    name: 'Spawn Burst',
    category: 'spawn',
    contexts: ['spawn'],
    description: 'Spawn a burst of particles at once',
    color: '#4CAF50',
    inputs: [
      dataPort('count', 'Count', 'int', { defaultValue: 10, range: { min: 1, max: 10000 } }),
      dataPort('delay', 'Delay', 'float', { defaultValue: 0, range: { min: 0, max: 10 } }),
      dataPort('repeat', 'Repeat', 'int', { defaultValue: 1, range: { min: 1, max: 100 } }),
      dataPort('interval', 'Interval', 'float', { defaultValue: 1, range: { min: 0, max: 10 } }),
    ],
    outputs: [
      flowPort('spawn', 'Spawn'),
    ],
    modifiesParticle: false,
  },

  // ---- Spawn Over Distance ----
  {
    id: 'vfx.spawn.distance',
    name: 'Spawn Over Distance',
    category: 'spawn',
    contexts: ['spawn'],
    description: 'Spawn particles based on emitter movement',
    color: '#4CAF50',
    inputs: [
      dataPort('rate', 'Rate', 'float', { defaultValue: 10, range: { min: 0, max: 1000 } }),
    ],
    outputs: [
      flowPort('spawn', 'Spawn'),
    ],
    modifiesParticle: false,
  },

  // ---- Single Burst ----
  {
    id: 'vfx.spawn.single',
    name: 'Single Burst',
    category: 'spawn',
    contexts: ['spawn'],
    description: 'Spawn a fixed number of particles once after an optional delay',
    color: '#4CAF50',
    inputs: [
      dataPort('count', 'Count', 'int', { defaultValue: 100, range: { min: 1, max: 100000 } }),
      dataPort('delay', 'Delay', 'float', { defaultValue: 0, range: { min: 0, max: 100 } }),
    ],
    outputs: [
      flowPort('spawn', 'Spawn'),
    ],
    modifiesParticle: false,
  },

  // ---- Periodic Burst ----
  {
    id: 'vfx.spawn.periodic',
    name: 'Periodic Burst',
    category: 'spawn',
    contexts: ['spawn'],
    description: 'Spawn bursts of particles repeatedly at regular intervals',
    color: '#4CAF50',
    inputs: [
      dataPort('count', 'Count', 'int', { defaultValue: 10, range: { min: 1, max: 10000 } }),
      dataPort('period', 'Period', 'float', { defaultValue: 1, range: { min: 0.01, max: 100 } }),
      dataPort('delay', 'Delay', 'float', { defaultValue: 0, range: { min: 0, max: 100 } }),
    ],
    outputs: [
      flowPort('spawn', 'Spawn'),
    ],
    modifiesParticle: false,
  },

  // ---- GPU Event ----
  {
    id: 'vfx.spawn.gpu_event',
    name: 'GPU Event',
    category: 'spawn',
    contexts: ['spawn'],
    description: 'Spawn from GPU event (particle death, collision)',
    color: '#4CAF50',
    inputs: [
      dataPort('event', 'Event', 'int', { defaultValue: 0, options: ['OnDeath', 'OnCollision'] }),
      dataPort('ratio', 'Ratio', 'float', { defaultValue: 1, range: { min: 0, max: 1 } }),
    ],
    outputs: [
      flowPort('spawn', 'Spawn'),
    ],
    modifiesParticle: false,
  },
];
